/**
 * クリニック編集時のライセンス差分適用・登録サマリ用ユーティリティ
 */

import { collectStripeWarnings, buildSaveMessageWithStripeWarnings } from './stripeWarningUtils.js';

function emptyToBlank(value) {
  if (value == null || value === '') return '';
  return String(value);
}

/**
 * 契約タイムスタンプ等を date input 用の YYYY-MM-DD にする
 * @param {number|string|Date|null|undefined} ts
 * @returns {string}
 */
export function toLicenseDateInputValue(ts) {
  if (ts == null || ts === '') return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  // 既存 UI と同じく UTC 日付（サーバーが date-only を UTC で保存する前提）
  return d.toISOString().split('T')[0];
}

/**
 * 差分比較用にライセンス状態を正規化する
 * @param {object} license
 * @returns {{ name: string, status: string, trialEndAt: string, billingDeferralUntil: string, billingDeferralReason: string }}
 */
export function normalizeLicenseForCompare(license) {
  const status = String(license?.status || 'active');
  return {
    name: String(license?.name || ''),
    status,
    // ステータスに意味のないフィールドは比較対象外（残留値による誤差分を防ぐ）
    trialEndAt: status === 'trial' ? emptyToBlank(license?.trialEndAt) : '',
    billingDeferralUntil: status === 'active' ? emptyToBlank(license?.billingDeferralUntil) : '',
    billingDeferralReason: status === 'active' ? emptyToBlank(license?.billingDeferralReason) : '',
  };
}

/**
 * @param {object} a
 * @param {object} b
 * @returns {boolean}
 */
export function isSameLicenseState(a, b) {
  const na = normalizeLicenseForCompare(a);
  const nb = normalizeLicenseForCompare(b);
  return na.name === nb.name
    && na.status === nb.status
    && na.trialEndAt === nb.trialEndAt
    && na.billingDeferralUntil === nb.billingDeferralUntil
    && na.billingDeferralReason === nb.billingDeferralReason;
}

/**
 * @param {Array} results
 * @returns {boolean}
 */
export function hasLicenseApplyFailure(results = []) {
  return results.some((result) => result && result.ok === false);
}

/**
 * 保存成功した add/update について、フォーム値をサーバー状態へ同期する。
 * - 成功 upsert: サーバー値で上書き（自動設定の trialEndAt 等を反映）
 * - 失敗 upsert/add: フォーム値を維持（再試行のため）
 * - 削除失敗でフォームから消えているもの: サーバーにあっても戻さない
 * @param {Array} formLicenses
 * @param {Array} serverLicenses
 * @param {Array<{ licenseName: string, action: string, ok: boolean }>} results
 * @returns {Array}
 */
export function syncFormLicensesAfterApply(formLicenses = [], serverLicenses = [], results = []) {
  const successfulUpsertNames = new Set(
    results
      .filter((result) => result?.ok && (result.action === 'add' || result.action === 'update'))
      .map((result) => result.licenseName)
  );
  const serverByName = new Map(
    (serverLicenses || [])
      .filter((license) => license?.name)
      .map((license) => [license.name, license])
  );

  return (formLicenses || []).map((license) => {
    if (!license?.name) return license;
    if (successfulUpsertNames.has(license.name) && serverByName.has(license.name)) {
      return serverByName.get(license.name);
    }
    return license;
  });
}

/**
 * 編集モーダル open 時と同じ規則でベースライン選択ライセンスを構築する
 * @param {object} params
 * @param {object} params.clinicLicenses - clinic.licenses
 * @param {number} params.licenseLimit
 * @param {Array} params.contracts - licenseContracts
 * @param {(licenses: object, limit: number) => Array<{name: string}>} params.getActiveLicenses
 * @returns {Array<{name: string, status: string, trialEndAt: string, trialSource: string|null, billingDeferralUntil: string, billingDeferralReason: string|null}>}
 */
export function buildBaselineSelectedLicenses({
  clinicLicenses,
  licenseLimit,
  contracts = [],
  getActiveLicenses,
}) {
  const mapContractToLicense = (contract) => ({
    name: contract.licenseName,
    status: contract?.status || 'active',
    trialEndAt: toLicenseDateInputValue(contract?.trialEndAt),
    trialSource: contract?.trialSource || null,
    billingDeferralUntil: toLicenseDateInputValue(contract?.billingDeferralUntil),
    billingDeferralReason: contract?.billingDeferralReason || null,
  });

  const activeLicenses = getActiveLicenses(clinicLicenses || {}, licenseLimit);
  const activeNames = new Set(activeLicenses.map((l) => l.name));
  const selectedFromSlots = activeLicenses.map((lic) => {
    const contract = contracts.find((c) => c.licenseName === lic.name);
    return {
      name: lic.name,
      status: contract?.status || 'active',
      trialEndAt: toLicenseDateInputValue(contract?.trialEndAt),
      trialSource: contract?.trialSource || null,
      billingDeferralUntil: toLicenseDateInputValue(contract?.billingDeferralUntil),
      billingDeferralReason: contract?.billingDeferralReason || null,
    };
  });
  const selectedFromContractsOnly = contracts
    .filter((c) => c.licenseName && !activeNames.has(c.licenseName))
    .map(mapContractToLicense);

  return [...selectedFromSlots, ...selectedFromContractsOnly];
}

/**
 * @param {Array} baselineLicenses
 * @param {Array} nextLicenses
 * @returns {{ toRemove: Array, toAdd: Array, toUpdate: Array, toSkip: Array }}
 */
export function buildLicenseDiff(baselineLicenses = [], nextLicenses = []) {
  const baselineByName = new Map();
  for (const license of baselineLicenses) {
    const name = String(license?.name || '');
    if (!name) continue;
    baselineByName.set(name, license);
  }

  const nextByName = new Map();
  for (const license of nextLicenses) {
    const name = String(license?.name || '');
    if (!name) continue;
    nextByName.set(name, license);
  }

  const toRemove = [];
  const toAdd = [];
  const toUpdate = [];
  const toSkip = [];

  for (const [name, base] of baselineByName.entries()) {
    if (!nextByName.has(name)) {
      toRemove.push(base);
    }
  }

  for (const [name, next] of nextByName.entries()) {
    if (!baselineByName.has(name)) {
      toAdd.push(next);
      continue;
    }
    if (!isSameLicenseState(baselineByName.get(name), next)) {
      toUpdate.push(next);
    } else {
      toSkip.push(next);
    }
  }

  return { toRemove, toAdd, toUpdate, toSkip };
}

const ACTION_LABELS = {
  add: '追加',
  update: '更新',
  remove: '削除',
  skip: 'スキップ',
};

/**
 * 差分に対して API を順次実行する（失敗しても継続）
 * @param {object} params
 * @param {string} params.clinicId
 * @param {{ toRemove: Array, toAdd: Array, toUpdate: Array, toSkip: Array }} params.diff
 * @param {{ removeClinicLicense: Function, addClinicLicense: Function }} params.adminRepo
 * @returns {Promise<Array<{ licenseName: string, action: string, ok: boolean, message?: string, data?: object }>>}
 */
export async function applyClinicLicenseDiff({ clinicId, diff, adminRepo }) {
  const results = [];

  for (const license of diff.toSkip || []) {
    results.push({
      licenseName: license.name,
      action: 'skip',
      ok: true,
    });
  }

  for (const license of diff.toRemove || []) {
    const licenseName = license.name;
    try {
      const data = await adminRepo.removeClinicLicense(clinicId, licenseName);
      results.push({ licenseName, action: 'remove', ok: true, data });
    } catch (err) {
      results.push({
        licenseName,
        action: 'remove',
        ok: false,
        message: err?.message || '削除に失敗しました',
      });
    }
  }

  for (const license of diff.toAdd || []) {
    try {
      const data = await adminRepo.addClinicLicense(clinicId, license.name, {
        status: license.status,
        trialEndAt: license.trialEndAt || null,
        billingDeferralUntil: license.billingDeferralUntil || null,
        billingDeferralReason: license.billingDeferralReason || null,
      });
      results.push({ licenseName: license.name, action: 'add', ok: true, data });
    } catch (err) {
      results.push({
        licenseName: license.name,
        action: 'add',
        ok: false,
        message: err?.message || '追加に失敗しました',
      });
    }
  }

  for (const license of diff.toUpdate || []) {
    try {
      const data = await adminRepo.addClinicLicense(clinicId, license.name, {
        status: license.status,
        trialEndAt: license.trialEndAt || null,
        billingDeferralUntil: license.billingDeferralUntil || null,
        billingDeferralReason: license.billingDeferralReason || null,
      });
      results.push({ licenseName: license.name, action: 'update', ok: true, data });
    } catch (err) {
      results.push({
        licenseName: license.name,
        action: 'update',
        ok: false,
        message: err?.message || '更新に失敗しました',
      });
    }
  }

  return results;
}

/**
 * 新規作成時のライセンス一括追加（失敗しても継続）
 * @param {object} params
 * @param {string} params.clinicId
 * @param {Array} params.licenses
 * @param {{ addClinicLicense: Function }} params.adminRepo
 */
export async function applyClinicLicenseCreates({ clinicId, licenses = [], adminRepo }) {
  const results = [];
  for (const license of licenses) {
    try {
      const data = await adminRepo.addClinicLicense(clinicId, license.name, {
        status: license.status,
        trialEndAt: license.trialEndAt || null,
        billingDeferralUntil: license.billingDeferralUntil || null,
        billingDeferralReason: license.billingDeferralReason || null,
      });
      results.push({ licenseName: license.name, action: 'add', ok: true, data });
    } catch (err) {
      results.push({
        licenseName: license.name,
        action: 'add',
        ok: false,
        message: err?.message || '追加に失敗しました',
      });
    }
  }
  return results;
}

/**
 * @param {Array} results
 * @returns {{ added: number, updated: number, removed: number, skipped: number, failed: number, successOps: number }}
 */
export function summarizeLicenseResults(results = []) {
  const counts = {
    added: 0,
    updated: 0,
    removed: 0,
    skipped: 0,
    failed: 0,
    successOps: 0,
  };

  for (const result of results) {
    if (!result.ok) {
      counts.failed += 1;
      continue;
    }
    if (result.action === 'add') {
      counts.added += 1;
      counts.successOps += 1;
    } else if (result.action === 'update') {
      counts.updated += 1;
      counts.successOps += 1;
    } else if (result.action === 'remove') {
      counts.removed += 1;
      counts.successOps += 1;
    } else if (result.action === 'skip') {
      counts.skipped += 1;
    }
  }

  return counts;
}

/**
 * @param {object} params
 * @param {string} params.baseMessage
 * @param {Array} params.results
 * @param {Array} [params.stripeWarnings]
 * @returns {{ message: string, variant: 'success'|'warning', title: string }}
 */
export function buildLicenseSaveAlert({ baseMessage, results = [], stripeWarnings }) {
  const counts = summarizeLicenseResults(results);
  const lines = [baseMessage, ''];

  lines.push(
    `【ライセンス】追加 ${counts.added} / 更新 ${counts.updated} / 削除 ${counts.removed} / 変更なし ${counts.skipped} / 失敗 ${counts.failed}`
  );

  const detailResults = results.filter((r) => r.action !== 'skip');
  if (detailResults.length > 0) {
    lines.push('');
    for (const result of detailResults) {
      const actionLabel = ACTION_LABELS[result.action] || result.action;
      if (result.ok) {
        lines.push(`・${result.licenseName} — ${actionLabel}成功`);
      } else {
        lines.push(`・${result.licenseName} — ${actionLabel}失敗: ${result.message || '不明なエラー'}`);
      }
    }
  }

  const warnings = stripeWarnings ?? collectStripeWarnings(
    results.filter((r) => r.ok).map((r) => r.data).filter(Boolean)
  );
  const message = buildSaveMessageWithStripeWarnings(lines.join('\n').trimEnd(), warnings);
  const hasFailure = counts.failed > 0;

  return {
    message,
    variant: hasFailure ? 'warning' : 'success',
    title: hasFailure ? '保存完了（一部失敗）' : '保存完了',
  };
}
